import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PHOTO_ABOUT, WHATSAPP_URL } from "../data/site";
import SEO from "../seo/SEO";
import {
  physicianSchema,
  medicalClinicSchemas,
  breadcrumbSchema,
  webPageSchema,
} from "../seo/schemas";

const TIMELINE = [
  { year: "MBBS", t: "Foundation in clinical medicine" },
  { year: "MD", t: "Internal Medicine — broad-base internist training" },
  { year: "DM", t: "Gastroenterology — Banaras Hindu University (BHU)" },
  { year: "Practice", t: "10+ years dedicated GI, Liver & Endoscopy practice in Varanasi" },
];

const MEMBERSHIPS = [
  { abbr: "ISG", full: "Indian Society of Gastroenterology" },
  { abbr: "INASL", full: "Indian National Association for Study of the Liver" },
  { abbr: "SGEI", full: "Society of GI Endoscopy of India" },
];

const FOCUS = [
  "Diagnostic & therapeutic upper GI endoscopy",
  "Colonoscopy & polypectomy",
  "ERCP for biliary and pancreatic disease",
  "EUS — endoscopic ultrasound",
  "Hepatitis B & C — chronic management",
  "Fatty liver and cirrhosis care",
  "IBD — Ulcerative Colitis & Crohn's",
  "Pancreatic & gallbladder disease",
];

export default function About() {
  return (
    <main data-testid="about-page">
      <SEO
        title="About Dr. Indresh Dixit — DM Gastroenterologist (BHU), Varanasi"
        description="Dr. Indresh Dixit — DM Gastroenterology from BHU, 10+ years in advanced GI, liver and endoscopy practice in Varanasi. ISG, INASL, SGEI member."
        canonical="/about"
        schemas={[
          physicianSchema(),
          ...medicalClinicSchemas(),
          webPageSchema({
            name: "About Dr. Indresh Dixit",
            description:
              "DM Gastroenterology (BHU), 10+ years GI/Liver/Endoscopy practice in Varanasi.",
            url: "/about",
          }),
          breadcrumbSchema([
            { label: "Home", url: "/" },
            { label: "About Dr. Indresh Dixit" },
          ]),
        ]}
      />
      {/* Header */}
      <section className="section-pad border-b border-black/10">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="overline">About Dr. Indresh Dixit</div>
            <h1 className="font-serif-display mt-5 text-5xl sm:text-6xl lg:text-[72px] leading-[1.02] tracking-tight">
              DM Gastroenterologist &amp; Liver Specialist in{" "}
              <span className="text-[#0370AD]">Varanasi.</span>
            </h1>
            <p className="mt-8 text-black/70 leading-relaxed text-[17px] max-w-2xl">
              Dr. Indresh Dixit is a DM Gastroenterologist trained at Banaras
              Hindu University with a decade of dedicated practice in digestive
              and liver disease. The work is deliberate — long consults,
              detailed work-ups, plain-language explanations, and follow-ups
              that actually happen.
            </p>
            <p className="mt-5 text-black/70 leading-relaxed text-[17px] max-w-2xl">
              The practice runs out of two Varanasi clinics — Lanka and
              Chetganj — supported by partner hospitals for endoscopy and
              admission. The emphasis is on getting the diagnosis right the
              first time and avoiding unnecessary procedures.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-primary group"
                data-testid="about-book-btn"
              >
                Book a Consultation <ArrowUpRight className="h-4 w-4 arrow" />
              </a>
              <Link to="/contact" className="btn-ghost" data-testid="about-contact-link">
                View Locations
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden border border-black/10 rounded-[6px]">
              <img
                src={PHOTO_ABOUT}
                alt="Dr. Indresh Dixit"
                className="h-full w-full object-cover"
                data-testid="about-photo"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Training Timeline */}
      <section className="section-pad bg-[#fbfaf7] border-b border-black/10">
        <div className="container-x">
          <div className="overline">Training &amp; Practice</div>
          <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            Education, training and practice timeline.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10 rounded-[6px] overflow-hidden">
            {TIMELINE.map((t, i) => (
              <div
                key={i}
                data-testid={`timeline-${i}`}
                className="bg-[#fbfaf7] p-7"
              >
                <div className="text-[11px] tracking-[0.2em] uppercase text-[#0370AD] font-semibold">
                  Stage {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-5 font-display-semibold text-2xl">{t.year}</h3>
                <p className="mt-3 text-black/65 text-[14px] leading-relaxed">
                  {t.t}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Memberships & Focus */}
      <section className="section-pad">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline">Memberships</div>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl tracking-tight">
              ISG · INASL · SGEI member, Varanasi.
            </h2>
            <div className="mt-10 space-y-5">
              {MEMBERSHIPS.map((m) => (
                <div
                  key={m.abbr}
                  data-testid={`membership-${m.abbr}`}
                  className="flex items-baseline gap-6 border-b border-black/10 pb-4"
                >
                  <span className="font-serif-display text-4xl text-[#0370AD]">
                    {m.abbr}
                  </span>
                  <span className="text-[15px] text-black/75">{m.full}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="overline">Areas of Clinical Focus</div>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl tracking-tight">
              GI &amp; liver focus areas.
            </h2>
            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {FOCUS.map((f, i) => (
                <li
                  key={i}
                  data-testid={`focus-${i}`}
                  className="flex items-start gap-3 border-b border-dashed border-black/10 pb-3"
                >
                  <span className="text-[11px] tracking-[0.18em] uppercase text-[#0370AD] font-semibold pt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[15px] text-black/80">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </main>
  );
}
